import React, {FC} from "react";
import {Colors, Container, Divider, IconEmptyCheck, IconLogout, Text} from "@bounce/shared/ui";
import {PartnersGroupItem} from "../declarations";
import {PartnerSwitcherPopover} from "./PartnerSwitcherPopover";

interface PartnerSwitcherDropdownProps {
  partners: PartnersGroupItem[];
  currentPartnerCode: string;
  currentPartnerName: string;
  currentPartnerType: string;
  currentPartnerSymbol: string | undefined;
  email: string;
  onSelect: (partnerCode: string) => void;
  onClose: () => void;
  onSignOut: () => void;
  anchorEl: HTMLElement | null;
  open: boolean;
}

const PartnerSwitcherDropdown: FC<PartnerSwitcherDropdownProps> = ({
  partners,
  currentPartnerCode,
  currentPartnerName,
  currentPartnerSymbol,
  email,
  onSelect,
  onClose,
  onSignOut,
  anchorEl,
  open,
}) => {
  return (
    <PartnerSwitcherPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            marginLeft: "248px",
            width: "240px",
          },
        },
      }}
    >
      <Container sx={{flexDirection: "column", gap: 0, alignItems: "stretch"}}>
        <Container
          sx={{
            padding: "12px 16px",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-start",
          }}
        >
          <Container
            sx={{
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
            }}
          >
            {currentPartnerSymbol && (
              <img
                src={currentPartnerSymbol}
                alt={currentPartnerName}
                style={{width: "24px", height: "24px", borderRadius: "4px", flexShrink: 0}}
              />
            )}
            <Text size={16} variant="inter600" sx={{color: Colors.grey._1000, lineHeight: "20px"}}>
              {currentPartnerName}
            </Text>
          </Container>
          <Text size={12} variant="inter500" sx={{color: Colors.grey._600, lineHeight: "18px"}}>
            {email}
          </Text>
        </Container>
        {partners.length > 1 && (
          <>
            <Divider />
            <Container sx={{padding: "8px 16px", color: Colors.grey._600, fontSize: "12px", alignItems: "flex-start"}}>
              Partnerships
            </Container>
            <Container sx={{maxHeight: "300px", overflow: "auto", alignItems: "stretch", gap: 0}}>
              {partners.map((partner) => {
                const isSelected = partner.code === currentPartnerCode;
                return (
                  <Container
                    key={partner.code}
                    sx={{
                      flexDirection: "row",
                      padding: "12px",
                      backgroundColor: "transparent",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: Colors.secondary._100,
                      },
                    }}
                    onClick={() => {
                      if (!isSelected) {
                        onSelect(partner.code);
                      }
                    }}
                  >
                    {isSelected && <IconEmptyCheck color={Colors.secondary._800} width={20} height={20} />}
                    <Text
                      size={14}
                      variant="inter500"
                      sx={{
                        color: Colors.grey._1000,
                        lineHeight: "20px",
                        flex: 1,
                        marginLeft: isSelected ? "0" : "32px",
                      }}
                    >
                      {partner.display_name || partner.name}
                    </Text>
                  </Container>
                );
              })}
            </Container>
          </>
        )}
        <Divider />
        <Container
          sx={{
            flexDirection: "row",
            padding: "12px",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: Colors.secondary._100,
            },
          }}
          onClick={onSignOut}
        >
          <IconLogout color={Colors.red._900} style={{width: "20px", height: "20px"}} />
          <Text
            size={14}
            variant="inter500"
            sx={{
              color: Colors.red._900,
              lineHeight: "20px",
            }}
          >
            Sign out
          </Text>
        </Container>
      </Container>
    </PartnerSwitcherPopover>
  );
};

export default PartnerSwitcherDropdown;
